import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IContractClosingType, IContractDebtorInfo, IContractNote, IContractNoteEdit } from '../models';
import { BehaviorSubject, Observable, Subscription, combineLatest, finalize, map, shareReplay } from 'rxjs';
import { LocalizationService } from 'src/app/shared/localization/localization.service';
import { ContractsService } from '../contracts.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { FormsModule, NgForm } from '@angular/forms';
import { ValidatorService } from 'src/app/shared/validator.service';
import { ICanDeactivateComponent } from 'src/app/shared/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { SortEvent } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { ContractActionsComponent } from './contract-actions/contract-actions.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ControlErrorsComponent } from 'src/app/shared/control-errors/control-errors.component';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
  imports: [CommonModule, TranslateModule, ContractActionsComponent, SelectModule, TableModule, FormsModule, MatTooltipModule, ControlErrorsComponent]
})
export class NotesComponent implements OnInit, OnDestroy, ICanDeactivateComponent {

  @ViewChild('form') form: NgForm;
  @ViewChild('caseNotesTable') caseNotesTable: Table;

  contractStatusId: number;
  contractReference: string;
  contractClosingTypeId: number;

  closingTypes$: Observable<IContractClosingType[]>;
  format$ = this.localizationService.settings$;

  debtorInfo$: Observable<IContractDebtorInfo>;
  selectedNote: IContractNoteEdit;
  subs: Subscription[] = [];
  question: string;
  originalClosingType: IContractClosingType;
  selectedClosingType: IContractClosingType;
  closingTypeChanged: boolean;

  private refresh$ = new BehaviorSubject<void>(null);
  originalData$: Observable<IContractNote[]>;
  data$: Observable<IContractNote[]>;


  sortField: string = null;
  sortAscending = false;

  constructor(
    private localizationService: LocalizationService,
    private service: ContractsService,
    private messageToUi: MessageToUiService,
    private validator: ValidatorService,
    private translateServcie: TranslateService,
    private dialogRef: MatDialogRef<any>,
    private spinner: GlobalSpinnerService) { }

  canDeactivate(): boolean {
    return !this.form.dirty;
  }

  ngOnInit(): void {
    this.spinner.show();

    this.originalData$ = this.service.getContractNotes(this.contractStatusId).pipe(shareReplay(1));
    this.data$ = combineLatest([this.originalData$, this.refresh$])
      .pipe(map(([list]) => [...list]))

    this.closingTypes$ = this.service.getClosingTypes();
    this.debtorInfo$ = this.service.getContractDebtor(this.contractStatusId);
    this.question = this.translateServcie.instant('Contracts.Notes.Question');

    this.subs.push(this.service.getClosingTypes()
      .pipe(finalize(() => {
        this.spinner.hide();
      }))
      .subscribe({
        next: (_) => {
          this.selectedClosingType = _.find(c => c.closingTypeId == this.contractClosingTypeId);
          this.originalClosingType = this.selectedClosingType;
        },
        error: () => {
          this.messageToUi.genericError();
        }
      }));
    this.newNote();
  }

  ngOnDestroy(): void {
    this.subs?.forEach(x => x.unsubscribe());
    this.sendNewIdOnClose();
  }

  cancel() {
    if (this.selectedNote.text) {
      const result = confirm(this.question);
      if (result == true) {
        this.dialogRef.close();
      }
    }
    else {
      this.sendNewIdOnClose();
    }
  }

  sendNewIdOnClose() {
    if (this.closingTypeChanged && this.selectedClosingType != this.originalClosingType) {
      this.dialogRef.close(this.selectedClosingType.closingTypeId);
    }
    else {this.dialogRef.close(); }
  }

  editNote(note: IContractNote) {
    if (this.form.dirty) {
      const result = confirm(this.question);
      if (result == true) {
        this.selectedNote = { id: note.id, internal: note.internal, text: note.text } as IContractNoteEdit;
        this.form.control.markAsPristine();
      }
    }
    else {
      this.selectedNote = { id: note.id, internal: note.internal, text: note.text } as IContractNoteEdit;
      this.form.control.markAsPristine();
    }
  }

  newNote() {
    this.selectedNote = {} as IContractNoteEdit;
    this.form?.control.markAsPristine();
  }

  deleteNote(note: IContractNote) {
    this.subs.push(
      this.service.deleteNote(this.contractStatusId, note.id)
        .subscribe({
          next: (_) => {
            this.data$ = this.service.getContractNotes(this.contractStatusId);
            this.newNote();
          },
          error: (_) => { this.messageToUi.genericError(); }
        }));
  }

  save() {
    this.spinner.show()
    this.changeClosingType();
    const serviceCall = this.selectedNote.id ?
      this.service.updateNote(this.contractStatusId, this.selectedNote) :
      this.service.addNote(this.contractStatusId, this.selectedNote);
    this.subs.push(
      serviceCall.pipe(finalize(() => { this.spinner.hide(); }))
        .subscribe({
          next: (_) => {
            this.data$ = this.service.getContractNotes(this.contractStatusId);
            this.newNote();
          },
          error: (error) => { this.validator.handleError(this.form, error); }
        }));

  }

  changeClosingType() {
    if (this.originalClosingType != this.selectedClosingType) {
      this.service.changeClosingType(this.contractStatusId, this.selectedClosingType.closingTypeId).subscribe(
        {
          next: () => { },
          error: (error) => { this.validator.handleError(this.form, error); }
        }
      );
      this.closingTypeChanged = true;
    }
  }

  onInternalChange(selectedNote: IContractNoteEdit) {
    if (selectedNote.internal == true) {
      selectedNote.sendMessageToCaseHandler = false;
    }
  }

  onSendToCaseHandlerChange(selectedNote: IContractNoteEdit) {
    if (selectedNote.sendMessageToCaseHandler == true) {
      selectedNote.internal = false;
    }
  }

  onChangedClosingType(event, closingType: IContractClosingType) {
    const closingTypeChangedInfo =
      this.translateServcie.instant('ContractClosingType.Note.Text')
      + this.translateServcie.instant(closingType.closingTypeTranslationTag);

    if (this.selectedNote.text) {
      this.selectedNote.text = this.selectedNote.text += '\n' + closingTypeChangedInfo;
    }
    else {
      this.selectedNote.text = closingTypeChangedInfo;
    }
  }

  customSort(event: SortEvent) {
    const newSortField = event.field;
    const resetSorting = event.order > 0 && !this.sortAscending

    if (this.sortField === newSortField && this.sortField !== 'date' && resetSorting) {
      this.refresh$.next();
      this.sortField = null;
      this.sortAscending = null;
      this.caseNotesTable.sortField = 'date';
      this.caseNotesTable.sortOrder = -1;
    } else {
      this.sortField = newSortField;
      this.sortAscending = event.order > 0;
    }

    this.sortTableData(event)
  }

  sortTableData(event) {
    event.data.sort((data1, data2) => {
      const value1 = data1[event.field];
      const value2 = data2[event.field];
      let result = null;
      if (value1 == null && value2 != null) {result = -1; }
      else if (value1 != null && value2 == null) {result = 1; }
      else if (value1 == null && value2 == null) {result = 0; }
      else if (typeof value1 === 'string' && typeof value2 === 'string') {result = value1.localeCompare(value2); }
      else {result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0; }

      return event.order * result;
    });
  }

  toggleExpand(note: any): void {
    note.isExpanded = !note.isExpanded;
  }

  isTextLong(note: IContractNote): boolean {
    const enterCount = (note.text.match(/\n/g) || []).length;

    return enterCount >= 2 || note.text.length > 100;
  }
}
